import { useState, useRef, useEffect, useMemo, type ChangeEvent } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, Image, Save, Loader2, RefreshCw } from 'lucide-react';
import { ProductSelector } from '../product-management/ProductSelector';
import { FormSelection, type HubSpotForm } from '../hubspot-management/FormSelection';
import RevenueMatrix, { 
  type RevenueMatrixData, 
  createDefaultData, 
  serializeRevenueMatrix,
  MultiFormRevenueMatrix,
  type CampaignRevenueMatrices,
  serializeCampaignMatrices,
  deserializeCampaignMatrices
} from './RevenueMatrix';
import { type NavigationItem } from '../Navigation';

import { useCampaignApi, useHubSpotFormsApi, useApiService, type CreateCampaignRequest, type HubSpotFormField, type CampaignApi, type CampaignReferenceData, type TargetProfileOption } from '../../services/apiService';
import { TargetProfileSelector } from './TargetProfileSelector';
import { buildTargetProfileMap, buildTargetProfileSummary } from '../../utils/targetProfiles';
import { usePermissions } from '../../hooks/useAuth';
import { WebsitePathsEditor } from './WebsitePathsEditor';

interface CreateCampaignProps {
  onNavigate: (view: NavigationItem) => void;
}

// Minimal ConceptSummary type used by concepts API call
interface ConceptSummary {
  title: string;
}

interface NewCampaignState {
  name: string;
  businessUnit: string;
  brandId: number | null;
  category: string;
  concept: string;
  status: 'upcoming';
  startDate: string;
  endDate: string;
  budget: number;
  campaignType: string;
  platforms: string[];
  products: string[];
  description: string;
  image: string;
  targetMarkets: string[];
  b2bTargetProfileIds: number[];
  b2cTargetProfileIds: number[];
  websitePaths: string[]; // added
}


const businessUnitsList = [
  "Fresh Baltics",
  "Fresh Denmark",
  "Fresh Finland",
  "LU Australia",
  "LU BeNeFrItEs",
  "LU Denmark",
  "LU FIBA",
  "LU Germany",
  "LU Global",
  "LU Hungary",
  "LU Norway",
  "LU Poland",
  "LU Romania",
  "LU Sweden",
  "LU UK",
  "LU US"
];

const marketOptions = [
  // EU
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic", "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden",
  // UK
  "England", "Scotland", "Wales", "Northern Ireland",
  // US, Australia
  "United States", "Australia"
];

function formatDateDDMMYYYY(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

// simple path validation utility (optional UX validation)
function websitePathsValid(paths: string[]): boolean {
  return paths.every(p => p.startsWith('/') && !/\s/.test(p));
}

export default function CreateCampaign({ onNavigate }: CreateCampaignProps) {
  const [newCampaign, setNewCampaign] = useState<NewCampaignState>({
    name: '',
    businessUnit: 'LU Global',
    brandId: null,
    category: '',
    concept: 'none',
    status: 'upcoming',
    startDate: '',
    endDate: '',
    budget: 0,
    campaignType: '',
    platforms: [],
    products: [],
    description: '',
    image: '',
    targetMarkets: [],
    b2bTargetProfileIds: [],
    b2cTargetProfileIds: [],
    websitePaths: [] // init
  });

  const [referenceData, setReferenceData] = useState<CampaignReferenceData | null>(null);
  const [referenceLoading, setReferenceLoading] = useState(true);
  const [referenceError, setReferenceError] = useState<string | null>(null);

  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Revenue Matrix state
  const [revenueMatrixData, setRevenueMatrixData] = useState<CampaignRevenueMatrices>({ matrices: [] });

  // HubSpot Forms state
  const [hubSpotForms, setHubSpotForms] = useState<HubSpotForm[]>([]);
  const [selectedHubSpotForms, setSelectedHubSpotForms] = useState<Set<string>>(new Set());
  const [hubSpotFormsLoading, setHubSpotFormsLoading] = useState(false);
  const [hubSpotFormsError, setHubSpotFormsError] = useState<string | null>(null);

  // Concepts state
  const api = useApiService();
  const [conceptTitles, setConceptTitles] = useState<string[]>([]);
  const [conceptsLoading, setConceptsLoading] = useState(false);
  const [conceptsError, setConceptsError] = useState<string | null>(null);

  const campaignApi: CampaignApi = useCampaignApi();
  const hubSpotFormsApi = useHubSpotFormsApi();
  const { canManageHubSpot } = usePermissions();

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setReferenceLoading(true);
        setReferenceError(null);
        const data = await campaignApi.getReferenceData();
        setReferenceData(data);
      } catch (error) {
        console.error('Error loading campaign reference data:', error);
        setReferenceError('Failed to load target profiles and platforms. Please try again.');
      } finally {
        setReferenceLoading(false);
      }
    };

    loadReferenceData();
  }, []);

  useEffect(() => {
    if (!referenceData || referenceData.brands.length === 0) {
      return;
    }

    setNewCampaign(prev => {
      if (prev.brandId !== null) {
        return prev;
      }

      return {
        ...prev,
        brandId: referenceData.brands[0]?.id ?? null
      };
    });
  }, [referenceData]);

  const targetProfileMap = useMemo(() => {
    if (!referenceData) {
      return new Map<number, TargetProfileOption>();
    }

    return buildTargetProfileMap([
      ...(referenceData.b2bTargetProfiles ?? []),
      ...(referenceData.b2cTargetProfiles ?? [])
    ]);
  }, [referenceData]);

  const targetProfileSummary = useMemo(() => {
    if (targetProfileMap.size === 0) {
      return '';
    }

    return buildTargetProfileSummary(
      newCampaign.b2bTargetProfileIds,
      newCampaign.b2cTargetProfileIds,
      targetProfileMap
    );
  }, [newCampaign.b2bTargetProfileIds, newCampaign.b2cTargetProfileIds, targetProfileMap]);

  // Load HubSpot forms on component mount
  useEffect(() => {
    loadHubSpotForms();
  }, []);

  // Load concepts on mount
  useEffect(() => {
    const loadConcepts = async () => {
      setConceptsLoading(true);
      setConceptsError(null);
      try {
        const concepts = await api.get<ConceptSummary[]>('/concepts');
        setConceptTitles(concepts.map(c => c.title));
      } catch (err) {
        console.error('Error loading concepts:', err);
        setConceptsError('Failed to load concepts');
      } finally {
        setConceptsLoading(false);
      }
    };

    loadConcepts();
  }, [api]);

  const loadHubSpotForms = async () => {
    setHubSpotFormsLoading(true);
    setHubSpotFormsError(null);
    try {
      const forms = await hubSpotFormsApi.getForms();
      setHubSpotForms(forms);
    } catch (error) {
      console.error('Error loading HubSpot forms:', error);
      setHubSpotFormsError('Failed to load HubSpot forms. Please try refreshing.');
    } finally {
      setHubSpotFormsLoading(false);
    }
  };

  const handleRefreshHubSpotForms = async () => {
    setHubSpotFormsLoading(true);
    setHubSpotFormsError(null);
    try {
      await hubSpotFormsApi.refreshForms();
      await loadHubSpotForms(); // Reload forms after refresh
    } catch (error) {
      console.error('Error refreshing HubSpot forms:', error);
      setHubSpotFormsError('Failed to refresh HubSpot forms. Please try again.');
    } finally {
      setHubSpotFormsLoading(false);
    }
  };

  const handleHubSpotFormToggle = (formId: string, checked: boolean) => {
    const newSelection = new Set(selectedHubSpotForms);
    if (checked) {
      newSelection.add(formId);
    } else {
      newSelection.delete(formId);
    }
    setSelectedHubSpotForms(newSelection);
  };

  const handleHubSpotSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedHubSpotForms(new Set(hubSpotForms.map(form => form.id)));
    } else {
      setSelectedHubSpotForms(new Set());
    }
  };

  const handleRevenueMatrixChange = (data: CampaignRevenueMatrices) => {
    setRevenueMatrixData(data);
  };

  const loadFormFields = async (formId: string): Promise<HubSpotFormField[]> => {
    try {
      return await hubSpotFormsApi.getFormFields(formId);
    } catch (error) {
      console.error(`Error loading form fields for ${formId}:`, error);
      return [];
    }
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError('Please select an image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSubmitError('Image file size must be less than 10MB');
        return;
      }

      setSelectedImageFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      // Clear any previous error
      setSubmitError(null);
    }
  };

  const handleImageRemove = () => {
    setSelectedImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || newCampaign.brandId === null || !newCampaign.category || !newCampaign.startDate ||
        !newCampaign.endDate || !newCampaign.campaignType) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    if (new Date(newCampaign.startDate) >= new Date(newCampaign.endDate)) {
      setSubmitError('End date must be after start date');
      return;
    }

    if (newCampaign.b2bTargetProfileIds.length === 0 || newCampaign.b2cTargetProfileIds.length === 0) {
      setSubmitError('Please select at least one B2B and one B2C target profile');
      return;
    }

    if (!websitePathsValid(newCampaign.websitePaths)) {
      setSubmitError('Each website path must start with "/" and contain no spaces');
      return;
    }

    if (referenceLoading) {
      setSubmitError('Reference data is still loading. Please wait.');
      return;
    }

    if (!referenceData || targetProfileMap.size === 0) {
      setSubmitError('Campaign reference data is unavailable. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Create the campaign request object
      const campaignRequest: CreateCampaignRequest = {
        name: newCampaign.name,
        businessUnit: newCampaign.businessUnit,
        brandId: newCampaign.brandId!,
        category: newCampaign.category,
        concept: newCampaign.concept === 'none' ? undefined : newCampaign.concept,
        startDate: newCampaign.startDate,
        endDate: newCampaign.endDate,
        budget: newCampaign.budget,
        targetProfile: targetProfileSummary,
        campaignType: newCampaign.campaignType,
        b2bTargetProfileIds: newCampaign.b2bTargetProfileIds,
        b2cTargetProfileIds: newCampaign.b2cTargetProfileIds,
        platforms: newCampaign.platforms,
        products: newCampaign.products,
        description: newCampaign.description || undefined,
        targetMarkets: newCampaign.targetMarkets,
        hubSpotFormIds: Array.from(selectedHubSpotForms), // Add selected HubSpot form IDs
        revenueMatrix: serializeCampaignMatrices(revenueMatrixData), // Serialize revenue matrices
        websitePaths: newCampaign.websitePaths // new field
      };

      console.log('Creating campaign:', campaignRequest);
      console.log('With image file:', selectedImageFile?.name);
      console.log('Selected HubSpot forms:', Array.from(selectedHubSpotForms));
      console.log('Revenue matrix data:', revenueMatrixData);

      // Call the API to create the campaign with image upload
      const createdCampaign = await campaignApi.createCampaignWithImage(campaignRequest, selectedImageFile || undefined);
      
      console.log('Campaign created successfully:', createdCampaign);
      setSubmitSuccess(true);
      
      // Show success message briefly, then navigate back
      setTimeout(() => {
        onNavigate('campaign-dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating campaign:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to create campaign. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlatformChange = (platform: string, checked: boolean) => {
    if (checked) {
      setNewCampaign({
        ...newCampaign,
        platforms: [...newCampaign.platforms, platform]
      });
    } else {
      setNewCampaign({
        ...newCampaign,
        platforms: newCampaign.platforms.filter(p => p !== platform)
      });
    }
  };

  const handleProductsChange = (products: string[]) => {
    setNewCampaign({
      ...newCampaign,
      products: products
    });
  };

  const handleMarketChange = (market: string, checked: boolean) => {
    setNewCampaign(prev => ({
      ...prev,
      targetMarkets: checked
        ? [...prev.targetMarkets, market]
        : prev.targetMarkets.filter(m => m !== market)
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f5f0] p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Create New Campaign</h1>
              <p className="text-gray-600 mt-1">Fill out the form below to create a new marketing campaign</p>
            </div>
          </div>
          <Button 
            onClick={handleCreateCampaign} 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Campaign
              </>
            )}
          </Button>
        </div>

        {/* Error/Success Messages */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error creating campaign</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{submitError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {submitSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Campaign created successfully!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Redirecting to campaign dashboard...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Form */}
        <Card>
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Campaign Image */}
            <div className="space-y-2">
              <Label>Campaign Image (1920x1080)</Label>
              {imagePreview ? (
                <div className="space-y-3">
                  <div className="border-2 border-gray-300 rounded-lg p-4">
                    <img 
                      src={imagePreview} 
                      alt="Campaign preview" 
                      className="max-w-full h-48 object-contain mx-auto rounded"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{selectedImageFile?.name}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleImageRemove}
                      disabled={isSubmitting}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-blue-600 cursor-pointer hover:underline text-lg">Upload campaign image</p>
                    <p className="text-sm text-gray-500 mt-2">Recommended: 1920x1080 pixels</p>
                    <p className="text-xs text-gray-500 mt-1">Supported: JPEG, PNG, GIF, WebP (max 10MB)</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                </>
              )}
            </div>

            {/* Campaign Name and Business Unit */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  placeholder="Enter campaign name"
                  className="h-11"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessUnit">Business Unit *</Label>
                <Select 
                  value={newCampaign.businessUnit} 
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, businessUnit: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select business unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnitsList.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Brand and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="brand">Brand *</Label>
                <Select
                  value={newCampaign.brandId !== null ? newCampaign.brandId.toString() : ''}
                  onValueChange={(value) => setNewCampaign(prev => ({ ...prev, brandId: value ? Number(value) : null }))}
                  disabled={isSubmitting || referenceLoading || (referenceData?.brands.length ?? 0) === 0}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={referenceLoading ? 'Loading brands...' : 'Select brand'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(referenceData?.brands ?? []).map(brand => (
                      <SelectItem key={brand.id} value={brand.id.toString()}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!referenceLoading && (referenceData?.brands.length ?? 0) === 0 && (
                  <p className="text-xs text-red-600">No brands available. Please add a brand first.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category Segment *</Label>
                <Select 
                  value={newCampaign.category} 
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, category: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fast Food">Fast Food</SelectItem>
                    <SelectItem value="Danish Pastry">Danish Pastry</SelectItem>
                    <SelectItem value="French Pastry">French Pastry</SelectItem>
                    <SelectItem value="Wheat Bread">Wheat Bread</SelectItem>
                    <SelectItem value="Rye Bread">Rye Bread</SelectItem>
                    <SelectItem value="Savouries">Savouries</SelectItem>
                    <SelectItem value="Cakes & Desserts">Cakes & Desserts</SelectItem>
                    <SelectItem value="Soft Dough">Soft Dough</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Start Date and End Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newCampaign.startDate}
                  onChange={(e) => setNewCampaign({ ...newCampaign, startDate: e.target.value })}
                  className="h-11"
                  disabled={isSubmitting}
                />
                {newCampaign.startDate && (
                  <div className="text-xs text-gray-500">Selected: {formatDateDDMMYYYY(newCampaign.startDate)}</div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date *</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={newCampaign.endDate}
                  onChange={(e) => setNewCampaign({ ...newCampaign, endDate: e.target.value })}
                  className="h-11"
                  disabled={isSubmitting}
                />
                {newCampaign.endDate && (
                  <div className="text-xs text-gray-500">Selected: {formatDateDDMMYYYY(newCampaign.endDate)}</div>
                )}
              </div>
            </div>

            {/* Campaign Type and Concept */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="campaignType">Campaign Type *</Label>
                <Select 
                  value={newCampaign.campaignType} 
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, campaignType: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select campaign type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Conversions">Conversions</SelectItem>
                    <SelectItem value="Customer Acquisition">Customer Acquisition</SelectItem>
                    <SelectItem value="Event Registration">Event Registration</SelectItem>
                    <SelectItem value="Lead Generation">Lead Generation</SelectItem>
                    <SelectItem value="Lead Nurture">Lead Nurture</SelectItem>
                    <SelectItem value="Online Visibility">Online Visibility</SelectItem>
                    <SelectItem value="Retention/Loyalty">Retention/Loyalty</SelectItem>
                    <SelectItem value="Store Visits">Store Visits</SelectItem>
                    <SelectItem value="Brand Awareness">Brand Awareness</SelectItem>
                    <SelectItem value="Engagement">Engagement</SelectItem>
                    <SelectItem value="Website Traffic">Website Traffic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="concept">Campaign Concept (Optional)</Label>
                <Select 
                  value={newCampaign.concept} 
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, concept: value })}
                  disabled={isSubmitting || conceptsLoading}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={conceptsLoading ? 'Loading concepts...' : 'Select concept'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No concept</SelectItem>
                    {conceptsError && (
                      <SelectItem value="__error" disabled>Failed to load concepts</SelectItem>
                    )}
                    {conceptTitles.map((title) => (
                      <SelectItem key={title} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Target Profiles */}
            <div className="space-y-4">
              <Label>Target Profiles *</Label>
              {referenceError && (
                <div className="text-sm text-red-600">{referenceError}</div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TargetProfileSelector
                  title="B2B Target Profiles"
                  options={referenceData?.b2bTargetProfiles ?? []}
                  selectedIds={newCampaign.b2bTargetProfileIds}
                  onSelectionChange={(ids) => setNewCampaign(prev => ({ ...prev, b2bTargetProfileIds: ids }))}
                  disabled={isSubmitting || referenceLoading || !!referenceError}
                  loading={referenceLoading}
                />
                <TargetProfileSelector
                  title="Consumer Profiles"
                  options={referenceData?.b2cTargetProfiles ?? []}
                  selectedIds={newCampaign.b2cTargetProfileIds}
                  onSelectionChange={(ids) => setNewCampaign(prev => ({ ...prev, b2cTargetProfileIds: ids }))}
                  disabled={isSubmitting || referenceLoading || !!referenceError}
                  loading={referenceLoading}
                />
              </div>
              {newCampaign.b2bTargetProfileIds.length > 0 && newCampaign.b2cTargetProfileIds.length > 0 && targetProfileSummary && (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
                  {targetProfileSummary}
                </div>
              )}
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label htmlFor="budget">Budget (SEK) *</Label>
              <Input
                id="budget"
                type="number"
                value={newCampaign.budget || ''}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="h-11 max-w-md"
                disabled={isSubmitting}
              />
            </div>

            {/* Online Platforms */}
            <div className="space-y-3">
              <Label>Online Platforms</Label>
              <div className="border rounded-md bg-white p-3">
                {referenceLoading && (
                  <div className="text-sm text-gray-500">Loading online platforms...</div>
                )}
                {!referenceLoading && referenceError && (
                  <div className="text-sm text-red-600">Unable to load online platforms.</div>
                )}
                {!referenceLoading && !referenceError && (referenceData?.onlinePlatforms?.length ?? 0) === 0 && (
                  <div className="text-sm text-gray-500">No online platforms available.</div>
                )}
                {!referenceLoading && !referenceError && (referenceData?.onlinePlatforms?.length ?? 0) > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {referenceData?.onlinePlatforms.map(platform => (
                      <div key={platform.id} className="flex items-center space-x-3">
                        <Checkbox
                          id={`platform-${platform.id}`}
                          checked={newCampaign.platforms.indexOf(platform.name) !== -1}
                          onCheckedChange={(checked) => handlePlatformChange(platform.name, checked === true)}
                          disabled={isSubmitting || referenceLoading || !!referenceError}
                        />
                        <Label htmlFor={`platform-${platform.id}`} className="text-sm font-normal">{platform.name}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Products */}
            <ProductSelector
              selectedProducts={newCampaign.products}
              onProductsChange={handleProductsChange}
            />

            {/* Target Markets */}
            <div className="space-y-2">
              <Label>Target Markets</Label>
              <div className="border rounded p-3 bg-white">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {marketOptions.map(market => (
                    <label key={market} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newCampaign.targetMarkets.indexOf(market) !== -1}
                        onChange={e => handleMarketChange(market, e.target.checked)}
                        disabled={isSubmitting}
                      />
                      {market}
                    </label>
                  ))}
                </div>
                {newCampaign.targetMarkets.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    Selected: {newCampaign.targetMarkets.join(", ")}
                  </div>
                )}
              </div>
            </div>

            {/* HubSpot Forms Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>HubSpot Forms (Optional)</Label>
                {canManageHubSpot && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshHubSpotForms}
                    disabled={hubSpotFormsLoading || isSubmitting}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${hubSpotFormsLoading ? 'animate-spin' : ''}`} />
                    Refresh Forms
                  </Button>
                )}
              </div>
              
              {hubSpotFormsError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">{hubSpotFormsError}</p>
                </div>
              )}

              {selectedHubSpotForms.size > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    {selectedHubSpotForms.size} HubSpot form{selectedHubSpotForms.size !== 1 ? 's' : ''} selected for this campaign
                  </p>
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  <FormSelection
                    forms={hubSpotForms}
                    selectedForms={selectedHubSpotForms}
                    onFormToggle={handleHubSpotFormToggle}
                    onSelectAll={handleHubSpotSelectAll}
                    loading={hubSpotFormsLoading}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Revenue Matrix */}
            <div className="space-y-3">
              <MultiFormRevenueMatrix
                selectedFormIds={selectedHubSpotForms}
                formsData={hubSpotForms}
                campaignMatrices={revenueMatrixData}
                onChange={handleRevenueMatrixChange}
                onLoadFormFields={loadFormFields}
                disabled={isSubmitting}
              />
            </div>

            {/* Website Paths Editor */}
            <div className="space-y-2">
              <Label>Website Paths (Optional)</Label>
              <WebsitePathsEditor
                paths={newCampaign.websitePaths}
                onChange={(paths) => setNewCampaign(prev => ({ ...prev, websitePaths: paths }))}
                disabled={isSubmitting}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="Add campaign description, goals, or notes..."
                className="min-h-[120px] resize-none"
                disabled={isSubmitting}
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 pb-8">
          <Button 
            variant="outline" 
            onClick={() => onNavigate('campaign-dashboard')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCampaign} 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Campaign
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}